import type { ComponentProps, ReactNode, JSX } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams, Outlet } from 'react-router-dom'
import * as m from 'motion/react-m'

import { clsx } from 'clsx'

import {
  Flex,
  PageHeader,
  PageLayout,
  Tabs,
  pageHeaderStyles,
} from '@buffer-mono/popcorn'

import { ChannelError } from '~publish/components/ChannelError'
import { ChannelNeedsRefresh } from '~publish/pages/Community/components/ChannelRefresh/ChannelNeedsRefresh'
import { ChannelRefreshUnsuccessful } from '~publish/pages/Community/components/ChannelRefresh/ChannelRefreshUnsuccessful'
import { getEngageUrl } from '~publish/components/CommentsSidebar/ChannelNav/helpers'
import { isInstagramLoginChannel } from '~publish/helpers/channels'
import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import type { CommentsChannelView } from '~publish/legacy/routes'
import { useCommunityConfig } from '~publish/pages/Community/CommunityConfig'
import { useCommunityCountsContextOptional } from '~publish/contexts/CommunityCountsContext'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { AccessRestricted } from './AccessRestricted'
import { ChannelLockedError } from './ChannelLockedError'
import { BackfillLoadingBanner } from '../Comments/CommentsList/BackfillLoadingBanner'
import { useActiveBackfillChannel } from '../hooks/backfill/useActiveBackfillChannel'
import { CommentsByPostView } from '../Comments/CommentsByPost'
import { DMsView } from '../DMs'
import { CommunityFilters } from '../CommunityFilters'
import { PersistedStatusSync } from '../CommunityFilters/PersistedStatusSync'
import { useFilters } from '../CommunityFilters/useFilters'
import { CommentsList } from '../Comments/CommentsList'
import { CommentsOrPostsViewToggle } from '../Comments/CommentsOrPostsViewToggle'
import { CommunityTabBar } from '../components/CommunityTabBar'
import { NarrowHeaderSlot } from '../components/NarrowHeaderSlot'
import { MentionsCounterPromoBanner } from '../components/MentionsCounterPromoBanner'
import { CommunityPageTitle } from '../CommunityPageTitle'
import { CommentNotificationsBanner } from './CommentNotificationsBanner'
import { MentionsInstagramLoginEmptyState, MentionsList } from '../Mentions'
import { LastSyncActions } from './LastSyncActions'
import { useGetChannel } from '../hooks/comments/useGetChannel'
import { useMentionsNeedsRefresh } from '../hooks/mentions/useMentionsNeedsRefresh'
import { useAvailableTabs } from '../hooks/navigation/useAvailableTabs'
import { useFilterHandlers } from '../hooks/navigation/useFilterHandlers'
import { useTabNavigation } from '../hooks/navigation/useTabNavigation'
import { useCommentsTracking } from '../hooks/tracking/useCommentsTracking'
import { useBannerToCoachMarkFlow } from '../hooks/ui/useBannerToCoachMarkFlow'
import { NewCommentsAvailableButton } from '../NewCommentsAvailableButton'
import { NewMentionsAvailableButton } from '../NewMentionsAvailableButton'
import { SingleChannelNotAvailable } from './SingleChannelNotAvailable'
import { SingleChannelViewSkeleton } from './SingleChannelViewSkeleton'
import { usePersistedViewPreference } from '../hooks/usePersistedViewPreference'
import { ViewOnlyHintButton } from '../shared/ViewOnlyHintButton'
import { DEFAULT_TAB, TAB_LABELS } from '../types'
import type { CommunityTab } from '../types'
import { isGoogleBusinessChannel } from '../util/channelHelpers'
import { isValidCommunityTab } from '../util/typeGuards'

import styles from './SingleChannelView.module.css'

// Re-exported so the colocated tests can mock the query via its historical
// import surface (`query.mockData(GetChannelInfoForComments, …)`).
export { GetChannelInfoForComments } from './fragments/getChannelInfoForComments'

type ToggleViewActionProps = {
  channelId: string | undefined
  channelService: string | undefined
  view: CommentsChannelView
  handleToggleGroupChange: (value: string) => void
}

const ToggleViewAction = ({
  channelId,
  channelService,
  view,
  handleToggleGroupChange,
}: ToggleViewActionProps): JSX.Element | null => {
  // Hide toggle for GBP channels - reviews should only be shown in list view
  if (!channelId || channelService === 'googlebusiness') return null

  return (
    <CommentsOrPostsViewToggle
      channelId={channelId}
      value={view}
      handleToggleGroupChange={handleToggleGroupChange}
      className={styles.toggleViewAction}
    />
  )
}

type GetMainContentArgs = {
  channel: ComponentProps<typeof ChannelNeedsRefresh>['channel']
  refreshFailed: boolean
  needsRefresh: boolean
}

/**
 * Full-pane override for the comments area, or `undefined` to let the normal
 * feed render. Kept as a value (not a component) so callers can use
 * `mainContent ?? <feed>` and pass it to `CommentsByPostView`'s `mainContent`
 * prop — a JSX element is always truthy and would suppress the feed.
 *
 * Priority: refresh failed → needs refresh → normal feed.
 */
function getMainContent({
  channel,
  refreshFailed,
  needsRefresh,
}: GetMainContentArgs): ReactNode {
  if (refreshFailed) return <ChannelRefreshUnsuccessful channel={channel} />
  if (needsRefresh) return <ChannelNeedsRefresh channel={channel} />
  return undefined
}

const PageLayoutMotion = m.create(PageLayout)

export const SingleChannelView = (): JSX.Element | null => {
  const [searchParams] = useSearchParams()
  const availableTabs = useAvailableTabs()
  const [isResolving, setIsResolving] = useState(false)
  const tabParam = searchParams.get('tab')
  const tab: CommunityTab =
    isValidCommunityTab(tabParam) && availableTabs.includes(tabParam)
      ? tabParam
      : DEFAULT_TAB
  const { channelId, postId: selectedPostId } = useParams<{
    channelId: string
    view?: string
    postId?: string
  }>()
  const communityCounts = useCommunityCountsContextOptional()
  const channelCommentCount =
    channelId != null
      ? (communityCounts?.getChannelCount(channelId) ?? null)
      : null
  const channelMentionCount =
    channelId != null
      ? (communityCounts?.getChannelMentionCount(channelId) ?? null)
      : null
  const { data, dataState, error } = useGetChannel(channelId)
  // `data` is a `dataState` union; only the complete result carries the full
  // `Channel` shape the page renders (partial/streaming is a `DeepPartial`).
  // Until then we fall through to the skeleton below.
  const channel = dataState === 'complete' ? data.channel : undefined

  // While the channel is still loading, pass `undefined` so the hook doesn't
  // redirect a GBP URL to /posts or auto-persist before we know the service.
  const isGoogleBusiness = channel
    ? isGoogleBusinessChannel(channel)
    : undefined
  const { view, setViewPreference } = usePersistedViewPreference({
    isGoogleBusiness,
  })

  const { shouldShowCoachMark, triggerCoachMark, dismissCoachMark } =
    useBannerToCoachMarkFlow()

  const {
    actions: { setStatus, setSortOption, setPublishedAt },
  } = useFilters()

  // Google Business reviews are list-only, so the toggle is hidden there. The
  // service check has to gate the wrapper too: an empty ActionGroup is still a
  // flex item, so it would hold open the gap between action groups.
  const shouldShowViewToggle =
    channelId && tab === 'comments' && channel?.service !== 'googlebusiness'

  // Scopes bulk actions to the selected post in the by-post view.
  const postIdForView = view === 'posts' ? selectedPostId : undefined

  const memoizedChannels = useMemo(() => (channel ? [channel] : []), [channel])

  // Backfill banner sits at the panel level (above channel notices and the
  // list/by-post content); show it while this channel is syncing.
  const backfillChannel = useActiveBackfillChannel(memoizedChannels)

  // Bulk resolve may only target channels the actor can manage. A view-only
  // channel (viewComments without manageComments) is excluded, which both hides
  // the bulk-resolve option and keeps it out of the dismiss mutation — the
  // backend rejects an all-view-only batch.
  const bulkResolveChannelIds = useMemo(
    () =>
      memoizedChannels
        .filter((c) => canPerform(c, 'manageComments'))
        .map((c) => c.id),
    [memoizedChannels],
  )

  const { handleStatusChange, handleDateRangeChange, handleSortOptionChange } =
    useFilterHandlers({
      channels: memoizedChannels,
      filterActions: { setStatus, setSortOption, setPublishedAt },
    })

  const { handleTabChange, handleTabsChange } = useTabNavigation({
    channelId,
    availableTabs,
  })

  // Validate and redirect for invalid tabs
  useEffect(() => {
    if (tab && !availableTabs.includes(tab)) {
      handleTabChange(DEFAULT_TAB)
    }
  }, [availableTabs, handleTabChange, tab])

  const {
    config: {
      isSupportedInComments,
      isSupportedInEngage,
      hasSupportedMentionsChannel,
      channelsSettings,
      channelNeedsRefresh,
      channelRefreshFailed,
    },
    loading: configLoading,
  } = useCommunityConfig()

  const { trackCommentsFiltered } = useCommentsTracking()

  // When the channel must reconnect before mentions flow, the mentions panel
  // shows the refresh empty state; the filters go away with the feed because
  // there is nothing to filter or resolve.
  const { allChannelsNeedRefresh: mentionsNeedRefresh } =
    useMentionsNeedsRefresh(memoizedChannels)

  const handleToggleGroupChange = useCallback(
    (value: string): void => {
      if (value === 'posts' || value === 'comments') {
        setViewPreference(value)
        trackCommentsFiltered({
          channels: memoizedChannels.map((c) => ({
            id: c.id,
            service: c.service,
          })),
          filterType: 'viewMode',
          filterValue: value,
        })
      }
    },
    [setViewPreference, trackCommentsFiltered, memoizedChannels],
  )

  if (error) {
    return <ChannelError error={error} />
  }

  // Wait for the config too: a null `channelsSettings` means the query hasn't
  // resolved, which would make `isSupportedInComments` wrongly return false.
  if (!channel || configLoading || !channelsSettings) {
    return <SingleChannelViewSkeleton />
  }

  const isUnsupportedChannel = !isSupportedInComments(channel)

  if (isUnsupportedChannel && isSupportedInEngage(channel)) {
    window.location.href = getEngageUrl(channel.id)
    return null
  }

  // An unsupported channel has no feed to act on, so none of the page chrome
  // (header, tabs, filters, view toggle, posts sidebar) applies: render only
  // the empty state, centered in the content area. Checked before the locked
  // and permission states — those point at recoverable access problems, but an
  // unsupported channel has nothing to recover.
  if (isUnsupportedChannel) {
    return (
      <PageLayout className={styles.notAvailableLayout}>
        <PageLayout.Container
          size="narrow"
          className={styles.notAvailableContainer}
        >
          <SingleChannelNotAvailable channel={channel} />
        </PageLayout.Container>
      </PageLayout>
    )
  }

  if (channel.isLocked) {
    return <ChannelLockedError channel={channel} />
  }

  if (!canPerform(channel, 'viewComments')) {
    return <AccessRestricted channel={channel} />
  }

  // Refresh states keep the page chrome and only swap the main content.
  // `refreshFailed` (retry already failed) takes priority over `needsRefresh`.
  const refreshFailed = channelRefreshFailed(channel)
  const needsRefresh = !refreshFailed && channelNeedsRefresh(channel)

  const showTabBar = availableTabs.length > 1
  // Views that fill the viewport and manage their own scrolling, rather than
  // scrolling as one page: the by-post split panel and the Messages panes.
  const isSplitView = (tab === 'comments' && view === 'posts') || tab === 'dms'
  // The filter/sort/resolve toolbar acts on the feed. The mentions tab swaps
  // the feed for the not-supported state (the channel cannot surface
  // mentions) or the refresh empty state (the channel must reconnect), and
  // then the toolbar has nothing to act on. Messages scopes its own controls
  // to the conversation list. The sync control keeps rendering.
  const showFilters =
    tab === 'comments' ||
    (tab === 'mentions' &&
      hasSupportedMentionsChannel(memoizedChannels) &&
      !mentionsNeedRefresh)

  const mainContent = getMainContent({
    channel,
    refreshFailed,
    needsRefresh,
  })

  return (
    <>
      <PersistedStatusSync />
      <Outlet />
      <PageLayoutMotion
        layoutScroll
        className={clsx(isSplitView && styles.layout)}
      >
        <Tabs
          defaultValue={DEFAULT_TAB}
          value={tab}
          onChange={handleTabsChange}
          className={clsx(styles.tabs, isSplitView && styles.splitView)}
        >
          <PageHeader className={styles.pageHeader}>
            <CommentNotificationsBanner />
            <PageHeader.Row>
              <CommunityPageTitle channels={[channel]} />
              <PageHeader.Actions className={styles.headerActions}>
                <PageHeader.ActionGroup>
                  <ViewOnlyHintButton channels={memoizedChannels} />
                  <FeedbackWidget
                    id="comments-channel-1"
                    source="comments"
                    className={clsx(
                      styles.feedbackWidget,
                      pageHeaderStyles.hideOnNarrow,
                    )}
                  />
                  {/* Hidden on narrow: the sync control is the first thing
                      to drop when the header runs out of room. */}
                  <Flex
                    align="center"
                    className={pageHeaderStyles.hideOnNarrow}
                  >
                    <LastSyncActions channel={channel} />
                  </Flex>
                </PageHeader.ActionGroup>
                {shouldShowViewToggle && (
                  <NarrowHeaderSlot narrow={false}>
                    <PageHeader.ActionGroup>
                      <ToggleViewAction
                        channelId={channelId}
                        channelService={channel.service}
                        view={view}
                        handleToggleGroupChange={handleToggleGroupChange}
                      />
                    </PageHeader.ActionGroup>
                  </NarrowHeaderSlot>
                )}
              </PageHeader.Actions>
              {tab === 'mentions' && (
                <NewMentionsAvailableButton channelId={channelId ?? ''} />
              )}
              {tab === 'comments' && (
                <NewCommentsAvailableButton channelId={channelId ?? ''} />
              )}
            </PageHeader.Row>
            {/* Secondary row: tab list (when there's more than one tab) + filters. */}
            <PageHeader.Row className={styles.toolbarRow}>
              {showTabBar && (
                <CommunityTabBar
                  tabs={availableTabs}
                  value={tab}
                  onChange={handleTabsChange}
                  commentsCount={channelCommentCount}
                  mentionsCount={channelMentionCount}
                  mentionsSupported={hasSupportedMentionsChannel(
                    memoizedChannels,
                  )}
                />
              )}
              {showFilters && (
                <PageHeader.Actions className={styles.headerActions}>
                  <CommunityFilters
                    setStatus={handleStatusChange}
                    setSortOption={handleSortOptionChange}
                    setPublishedAt={handleDateRangeChange}
                    channelIds={bulkResolveChannelIds}
                    postId={postIdForView}
                    onResolvingChange={setIsResolving}
                    showBannerDismissedCoachMark={shouldShowCoachMark}
                    onBannerDismissedCoachMarkDismiss={dismissCoachMark}
                    channelService={channel.service}
                    entity={tab === 'mentions' ? 'mentions' : 'comments'}
                  />
                  {shouldShowViewToggle && (
                    <NarrowHeaderSlot narrow>
                      <ToggleViewAction
                        channelId={channelId}
                        channelService={channel.service}
                        view={view}
                        handleToggleGroupChange={handleToggleGroupChange}
                      />
                    </NarrowHeaderSlot>
                  )}
                </PageHeader.Actions>
              )}
            </PageHeader.Row>
          </PageHeader>
          <Tabs.Panel
            value="comments"
            aria-label={TAB_LABELS.comments}
            className={styles.panel}
          >
            {backfillChannel && (
              <BackfillLoadingBanner channel={backfillChannel} fullBleed />
            )}
            {view === 'posts' ? (
              // Full-width container so the posts view can host the sidepanel.
              <PageLayout.Container
                size="full"
                className={styles.postsContainer}
              >
                <CommentsByPostView
                  channels={memoizedChannels}
                  onNavigateToUnreplied={(): void =>
                    handleStatusChange('unreplied')
                  }
                  mainContent={mainContent}
                />
              </PageLayout.Container>
            ) : (
              <PageLayout.Container size="narrow">
                {mainContent ?? (
                  <CommentsList
                    channels={memoizedChannels}
                    onNavigateToUnreplied={() =>
                      handleStatusChange('unreplied')
                    }
                    isLoading={isResolving}
                    onBannerDismissed={triggerCoachMark}
                  />
                )}
              </PageLayout.Container>
            )}
          </Tabs.Panel>
          <Tabs.Panel
            value="mentions"
            aria-label={TAB_LABELS.mentions}
            className={styles.panel}
          >
            <PageLayout.Container size="narrow">
              {channel && isInstagramLoginChannel(channel) ? (
                <MentionsInstagramLoginEmptyState channel={channel} />
              ) : (
                <>
                  <MentionsCounterPromoBanner
                    count={channelMentionCount ?? 0}
                  />
                  <MentionsList channels={memoizedChannels} />
                </>
              )}
            </PageLayout.Container>
          </Tabs.Panel>
          <Tabs.Panel
            value="dms"
            aria-label={TAB_LABELS.dms}
            className={styles.panel}
          >
            {/* Full-width: the Messages view hosts its own panes. */}
            <PageLayout.Container size="full" className={styles.dmsContainer}>
              <DMsView channels={memoizedChannels} />
            </PageLayout.Container>
          </Tabs.Panel>
        </Tabs>
      </PageLayoutMotion>
    </>
  )
}
